import * as Sentry from '@sentry/react'
import { action, Action, thunk, Thunk } from 'easy-peasy'
import { BonusAmount, BonusType, BonusTypeRegistry } from '../../sharedComponents/src/DailyBonus/bonusTypes'
import {
    claimBonusForUser,
    getBonusAdditionalInfo,
    getBonusForUser,
} from '../../sharedComponents/src/DailyBonus/walletAPI'
import { StoreModel } from '../../store/store'

export interface DailyBonusModel {
    // State
    bonuses: BonusTypeRegistry
    hintsAmount: number
    initialHintAmounts: Array<BonusAmount>

    // Actions
    setBonus: Action<DailyBonusModel, BonusTypeRegistry>
    setHintsAmount: Action<DailyBonusModel, number>
    setInitialHintAmounts: Action<DailyBonusModel, Array<BonusAmount>>

    // Thunks: getBonus resolves to how the wallet call went (only 'transient' is worth retrying),
    // claimBonus to whether it succeeded
    getBonus: Thunk<DailyBonusModel, BonusType, undefined, StoreModel, Promise<WalletCallResult>>
    claimBonus: Thunk<DailyBonusModel, BonusType, undefined, StoreModel, Promise<boolean>>
    getAdditionalBonusInfo: Thunk<DailyBonusModel, BonusType, undefined, StoreModel>
}

export type WalletCallResult = 'ok' | 'transient' | 'rejected'
// A deterministic 4xx repeats on every retry/refetch: report each op+code once per session, then breadcrumbs.
const reportedRejections = new Set<string>()

// walletAPI throws { error: { code, message }, status } (status 0 = no response). The BE message can carry
// player ids / internal addresses, so it never goes into the title or fingerprint.
const reportWalletFailure = (op: 'get' | 'claim' | 'info', bonusType: BonusType, err: any): WalletCallResult => {
    if (!err?.error) {
        Sentry.captureException(err, { tags: { bonusType } })
        return 'rejected'
    }
    const status: number = err.status ?? 0
    const code = String(err.error.code ?? 'UNKNOWN')
    const tags = { bonusType, httpStatus: String(status), walletCode: code }
    if (!status) {
        Sentry.addBreadcrumb({
            category: 'wallet',
            level: 'warning',
            message: `Daily bonus ${op} failed: no response`,
            data: tags,
        })
        return 'transient'
    }
    if (status >= 500) {
        Sentry.captureMessage(`Daily bonus ${op} failed: HTTP 5xx`, {
            level: 'warning',
            fingerprint: ['daily-bonus', op, '5xx'],
            tags,
        })
        return 'transient'
    }
    if (reportedRejections.has(`${op}:${code}`)) {
        Sentry.addBreadcrumb({
            category: 'wallet',
            level: 'warning',
            message: `Daily bonus ${op} failed: ${code}`,
            data: tags,
        })
    } else {
        reportedRejections.add(`${op}:${code}`)
        const walletMessage = String(err.error.message ?? '')
            .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<id>')
            .replace(/\d{1,3}(\.\d{1,3}){3}(:\d+)?/g, '<ip>')
        Sentry.captureException(new Error(`Daily bonus ${op} failed: ${code}`), {
            fingerprint: ['daily-bonus', op, code],
            tags,
            extra: { walletMessage },
        })
    }
    return 'rejected'
}

export const DailyBonusModel: DailyBonusModel = {
    // State
    bonuses: {},
    hintsAmount: -1,
    initialHintAmounts: [],

    // Actions
    setBonus: action((state, bonus) => {
        Object.keys(bonus).forEach((key) => {
            const updateValue = bonus[key]
            if (updateValue === undefined) {
                // Remove bonus if update value is undefined
                delete state.bonuses[key]
            } else {
                // Add or update bonus
                state.bonuses[key] = updateValue
            }
        })
    }),

    setHintsAmount: action((state, payload) => {
        state.hintsAmount = payload
    }),

    setInitialHintAmounts: action((state, payload) => {
        state.initialHintAmounts = payload
    }),

    // Thunks
    getBonus: thunk(async (actions, bonusType, { getStoreState }) => {
        try {
            const store = getStoreState()
            const userId = store.userData.userData?.id
            if (userId) {
                const result = await getBonusForUser(userId, bonusType)
                actions.setBonus({ [bonusType]: result })
            } else {
                actions.setBonus({ [bonusType]: undefined })
            }
            return 'ok'
        } catch (error) {
            // keep the last known bonus: a failed fetch (even a 422) says nothing about the bonus itself
            return reportWalletFailure('get', bonusType, error)
        }
    }),

    claimBonus: thunk(async (actions, bonusType, { getStoreState }) => {
        try {
            const store = getStoreState()
            const userId = store.userData.userData?.id
            if (userId) {
                const result = await claimBonusForUser(userId, bonusType)
                // update bonus data if needed
                switch (bonusType) {
                    case BonusType.DAILY_BONUS_HINT:
                        actions.setHintsAmount(result.amount)
                        break
                    default:
                        break
                }
                actions.getBonus(bonusType)
                return true
            }
            return false
        } catch (error) {
            reportWalletFailure('claim', bonusType, error)
            return false
        }
    }),

    getAdditionalBonusInfo: thunk(async (actions, bonusType) => {
        try {
            const result = await getBonusAdditionalInfo(bonusType)
            actions.setInitialHintAmounts(result)
        } catch (error) {
            reportWalletFailure('info', bonusType, error)
        }
    }),
}
